import { useMemo } from "react";

import Debug from "@/react/debug";

import filterMap from "./filterMap";

export const hasValueInKeys = (obj, predicate) =>
  Object.keys(obj).some((key) => predicate(obj[key]));

export const ORDERING = {
  ASCENDING: (countEntry1, countEntry2) => countEntry1[1] - countEntry2[1],
  DESCENDING: (countEntry1, countEntry2) => countEntry2[1] - countEntry1[1],
};

/**
 * Creates an array from the given `data`, which represents a map of the values of the given data member to their counts.
 * The given `data` is expected to be an array of objects, each of which may contain a member
 * identified by the given `countedMember`. The goal is to know how many times each unique
 * value of `countedMember` occurs within all the `data`.
 *
 * The returned result is a map of the values of `countedMember` to their counts,
 * but in an array form. Each element of the array is itself an array containing two elements:
 * a unique value of `countedMember` and the number of times that value occurs in `data`.
 *
 * An optional ordering function can be provided to sort this returned array.
 * By default, it is sorted in descending order by the number of counts. Both
 * ascending and descending functions are provided through the `ORDERING` object defined above.
 *
 * @param data The data from which to count values of the given member
 * @param countedMember The member in the objects at each array index, for which values we want to count
 * @param orderingFn Optional ordering function by which to sort the final result
 * @returns Array containing the counts of each unique value for the given member appearing in the data
 */
const useCountMapArray = (data, countedMember, orderingFn) =>
  useMemo(() => {
    let mappedArray = [];
    if (data) {
      mappedArray = Object.entries(
        data.reduce((accMap, entry) => {
          if (countedMember in entry) {
            const key = entry[countedMember];
            if (!(key in accMap)) {
              accMap[key] = 0;
            }
            accMap[key] += 1;
          } else {
            Debug.warn(
              `Entry ${JSON.stringify(
                entry
              )} does not contain member to count '${countedMember}'`
            );
          }
          return accMap;
        }, {})
      ).sort(orderingFn ?? ORDERING.DESCENDING);
    }
    return mappedArray;
  }, [data, countedMember, orderingFn]);

export { filterMap, useCountMapArray };
